import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import { externalAuthorizations } from '@shared/helpers/integrations.helper';
import { IIntegration } from '@shared/interfaces/integration.interface';
import { Integration } from '@shared/models/integration.model';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { SpongeConfigService } from './sponge-config.service';

@Injectable({
  providedIn: 'root'
})
export class IntegrationsAuthorizationsService {
  public externalAuthorisations$: BehaviorSubject<
    IIntegration[]
  > = new BehaviorSubject<IIntegration[]>([]);

  private readonly DEFAULT_LOGO = 'https://via.placeholder.com/150x150?text=ATS';

  constructor(
    private readonly _http: HttpClient,
    private readonly spongeConfigService: SpongeConfigService
  ) {}

  /**
   * Get all integrations - merges backend Sponge integrations with UI metadata
   * Returns both available and authorized integrations
   */
  public getAll(): Observable<IIntegration[]> {
    return forkJoin({
      authorized: this.getAuthorized(),
      available: this.getAvailable()
    }).pipe(
      map(({ authorized, available }) => {
        // Merge authorized with available, marking which are authorized
        const mergedMap = new Map<string, IIntegration>();

        // Add all available integrations
        available.forEach(integration => {
          mergedMap.set(integration.id, integration);
        });

        // Mark authorized ones
        authorized.forEach(authIntegration => {
          const existing = mergedMap.get(authIntegration.id);
          if (existing) {
            mergedMap.set(authIntegration.id, {
              ...existing,
              isAuthorized: true,
              isSetup: true
            });
          } else {
            // Legacy integration not in Sponge
            mergedMap.set(authIntegration.id, authIntegration);
          }
        });

        return Array.from(mergedMap.values());
      }),
      tap((value: IIntegration[]) => this.externalAuthorisations$.next(value))
    );
  }

  /**
   * Get available integrations: Legacy (hardcoded) + Sponge (from backend)
   */
  public getAvailable(): Observable<IIntegration[]> {
    // Always start with legacy integrations
    const legacyIntegrations = externalAuthorizations.map(ea => new Integration(ea));

    // Try to fetch Sponge integrations and merge
    return this.spongeConfigService.getAvailableIntegrations().pipe(
      map((backendIntegrations: any[]) => {
        const spongeIntegrations = backendIntegrations.map(backend => {
          // Frontend helper metadata wins over the backend logo_url: asset
          // hosts are region-specific, so the shared YAML config can't hold
          // a URL that is correct for every environment
          const uiMetadata = externalAuthorizations.find(
            ea => ea.id === backend.ats_key
          );

          return new Integration({
            id: backend.ats_key,
            label: backend.ats_name || backend.ats_key,
            logo: uiMetadata?.logo || backend.logo_url || this.DEFAULT_LOGO,
            isAuthorized: false,
            isLoading: false,
            isSetup: false,
            isAvailable: backend.is_available !== false
          });
        });

        // Merge: Legacy first, then Sponge integrations
        return [...legacyIntegrations, ...spongeIntegrations];
      }),
      catchError((error) => {
        // If the Sponge API fails, degrade to legacy integrations only
        console.error('Failed to fetch Sponge integrations, using only legacy.', error);
        return of(legacyIntegrations);
      })
    );
  }

  /**
   * Get authorized integrations from backend
   * Returns both legacy and Sponge configured integrations
   */
  public getAuthorized(): Observable<IIntegration[]> {
    return this._http
      .get<{ attributes: { label: string } }[]>(
        `${environment.ENDPOINTS.BACK_END.V2}/external_authorizations`
      )
      .pipe(
        map((data: any) =>
          data.data.reduce((memo: any, value: any) => {
            // Handle both legacy (label) and Sponge (uses ats_key from attributes.label)
            const integrationId = value.attributes.label;
            const uiMetadata = externalAuthorizations.find(
              (ea) => ea.id === integrationId
            );

            memo.push(
              new Integration({
                id: integrationId,
                label: uiMetadata?.label || integrationId,
                logo: uiMetadata?.logo || this.DEFAULT_LOGO,
                isAuthorized: true,
                isSetup: true,
                isLoading: false
              })
            );

            return memo;
          }, [])
        ),
        // Must emit (not just complete) so getAll()'s forkJoin still fires
        catchError((error) => {
          console.error('Failed to fetch authorized integrations.', error);
          return of([] as IIntegration[]);
        })
      );
  }

  /**
   * Legacy method for backwards compatibility
   * Use getAll() instead
   */
  public get(): Observable<IIntegration[]> {
    return this.getAuthorized().pipe(
      tap((value: IIntegration[]) => this.externalAuthorisations$.next(value))
    );
  }
}
